import { Button, Form, Input, Select, Typography } from 'antd';
import type { ForwardRefRenderFunction } from 'react';
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { tryit } from 'radash';
import type { BaseFormExposeRef, BaseFormProps } from '../../../types';
import { ExtractionRangeEnum, extractionRangeOptions } from './const';
import { FieldItem } from './filed-item';

export interface FormType {
  extraction_range: keyof typeof ExtractionRangeEnum;
  pages: string;
  role_setting: string;
  rules: {
    type: 'TEXT' | 'TABLE';
    source: 'ALL' | 'TEXT' | 'TABLE';
    tag_code?: string;
    prompt?: string;
    table_code?: string;
    table_prompt?: string;
    table_tag_list?: {
      tag_code: string;
      prompt?: string;
    }[]
  }[];
}

export type LlmFormProps = BaseFormProps<FormType>;

export interface ReturnType {
  form: FormType;
}

const PanelForm: ForwardRefRenderFunction<
  BaseFormExposeRef<ReturnType>,
  LlmFormProps
> = ({ initialValue, readonly, node_id }, ref) => {
  const [form] = Form.useForm<FormType>();
  const [activeField, setActiveField] = useState<number>();
  useImperativeHandle(ref, () => ({
    assembleForm: async () => {
      const [err, values] = await tryit(form.validateFields)();
      if (err) return;

      return {
        form: values,
      };
    },
  }));

  useEffect(() => {
    form.setFieldsValue(
      initialValue || {
        extraction_range: 'ALL',
        rules: [],
      },
    );
  }, [initialValue, form]);

  return (
    <div className="px-6">
      <div className="flex justify-center items-center w-full">
        <Form
          disabled={readonly}
          layout="vertical"
          form={form}
          name="semantic-form"
          className="w-full"
          autoComplete="off"
          initialValues={{
            role_setting: `##角色：
你是一个智能文档处理专家，擅长对文档进行识别，并支持对关键信息的抽取。

##任务：
第一步：理解任务提示词需要抽取的字段，阅读文档。
第二步：输出文档中字段抽取的结果。`,
          }}
        >
          <div className="flex items-end gap-4">
            <Form.Item<FormType>
              label="抽取范围"
              name="extraction_range"
              className="basis-1/3"
            >
              <Select
                getPopupContainer={(el) => el.parentElement}
                placeholder="请选择抽取范围"
                options={extractionRangeOptions}
              />
            </Form.Item>

            <Form.Item noStyle shouldUpdate={() => true}>
              {(form) => {
                const range: FormType['extraction_range'] =
                  form.getFieldValue('extraction_range');
                if (range !== 'TARGET') return null;
                return (
                  <Form.Item<FormType>
                    name="pages"
                    label=""
                    className="basis-2/3"
                    rules={[
                      {
                        pattern: /^((\d+)|(\d+);|(\d+)-(\d+);?)+$/,
                        message: '请输入正确的格式如 1;3-5;7',
                        warningOnly: true,
                      },
                    ]}
                  >
                    <Input placeholder="请输入页号如 1;3-5;7" maxLength={32} />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </div>

          <Form.Item
            label="角色设定"
            name="role_setting"
            rules={[
              { required: true, message: '请输入角色设定', warningOnly: true },
            ]}
            tooltip="按照业务需求，设定大模型角色和任务。我们已经为你提供了一个通用提示词，你可以直接使用，也可以按照业务需求进行修改。"
          >
            <Input.TextArea
              placeholder=""
              autoSize={{ minRows: 3 }}
              maxLength={5500}
              className="whitespace-pre-wrap text-left leading-relaxed font-sans"
            />
          </Form.Item>

          <Form.List name="rules">
            {(fields, operations, meta) => (
              <>
                {fields?.map((field) => (
                  <FieldItem
                    key={field.key}
                    field={field}
                    prefix={[field.name]}
                    fullPrefix={['rules', field.name]}
                    operation={operations}
                    node_id={node_id}
                    readonly={readonly}
                    activeField={activeField ?? 0}
                    setActiveField={setActiveField}
                  />
                ))}
                <Button type="dashed" onClick={() => operations.add()} block>
                  + 添加字段和规则
                </Button>
              </>
            )}
          </Form.List>

          {/* <Form.Item noStyle shouldUpdate>
            {() => (
              <Typography>
                <pre>{JSON.stringify(form.getFieldsValue(), null, 2)}</pre>
              </Typography>
            )}
          </Form.Item> */}
        </Form>
      </div>
    </div>
  );
};

export default memo(forwardRef(PanelForm));
